/**
 * Admin Risk & Compliance API client  v2
 * ────────────────────────────────────────
 * Supports 5-tier system, weighted reason codes, v2 case schema,
 * evidence, audit log, and reason code taxonomy.
 */
import api from './api';

const BASE = '/admin/risk';

const get = (url: string, opts?: any): Promise<any> => api.get(url, opts);
const post = (url: string, data?: any): Promise<any> => api.post(url, data);

// ── Dashboard ──
export const getRiskDashboard = () => get(`${BASE}/dashboard`);

// ── Risk Events ──
export const getRiskEvents = (params?: {
  user_id?: string; tier?: string; kind?: string; subject_type?: string;
  page?: number; limit?: number;
}) => get(`${BASE}/events`, { params });

// ── User Risk Profiles ──
export const getRiskUsers = (params?: { tier?: string; search?: string; page?: number; limit?: number }) =>
  get(`${BASE}/users`, { params });

export const getRiskUserDetail = (userId: string) => get(`${BASE}/users/${userId}`);
export const rescoreUser = (userId: string) => post(`${BASE}/users/${userId}/rescore`);
export const freezeUser = (userId: string, reason: string) => post(`${BASE}/users/${userId}/freeze`, { reason });
export const unfreezeUser = (userId: string) => post(`${BASE}/users/${userId}/unfreeze`);

// ── Cases ──
export const getRiskCases = (params?: {
  status?: string; queue?: string; tier?: string; priority?: string;
  page?: number; limit?: number;
}) => get(`${BASE}/cases`, { params });

export const getRiskCaseDetail = (caseId: string) => get(`${BASE}/cases/${caseId}`);
export const assignCase = (caseId: string, assignTo: string | null) => post(`${BASE}/cases/${caseId}/assign`, { assignTo });
export const escalateCase = (caseId: string, data: { queue?: string; note?: string; priority?: string }) => post(`${BASE}/cases/${caseId}/escalate`, data);
export const resolveCase = (caseId: string, data: { disposition: string; rationale: string; decision?: string }) => post(`${BASE}/cases/${caseId}/resolve`, data);
export const addCaseEvidence = (caseId: string, data: { evidenceType?: string; source?: string; content?: string; uri?: string; metadata?: any }) => post(`${BASE}/cases/${caseId}/evidence`, data);

// ── Policies ──
export const getRiskPolicies = () => get(`${BASE}/policies`);
export const getActivePolicy = () => get(`${BASE}/policies/active`);
export const createPolicy = (data: any) => post(`${BASE}/policies`, data);
export const activatePolicy = (policyId: string) => post(`${BASE}/policies/${policyId}/activate`);

// ── Incidents ──
export const getRiskIncidents = () => get(`${BASE}/incidents`);
export const createIncident = (data: { name: string; description?: string; context_boost?: number; freeze_payouts?: boolean }) => post(`${BASE}/incidents`, data);
export const resolveIncident = (id: string) => post(`${BASE}/incidents/${id}/resolve`);

// ── Audit Log ──
export const getAuditLog = (params?: { entity_type?: string; entity_id?: string; page?: number; limit?: number }) => get(`${BASE}/audit`, { params });

// ── Reason Code Taxonomy ──
export const getReasonCodeTaxonomy = () => get(`${BASE}/reason-codes`);

// ── Manual Score ──
export const manualScore = (data: { userId: string; eventKind?: string; amount_cents?: number }) => post(`${BASE}/score`, data);
